import { STAYS_WISHLIST } from "@capone/experiments";
import { Box } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useDeviceTypes, VibeCallToActionSection } from "halifax";
import {
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useGetVibesQuery,
} from "@capone/common";
import {
  AVAILABLE,
  getExperimentVariant,
  LC_GATING_EXPERIMENT,
  useExperiments,
} from "../../context/experiments";
import { PATH_HOME } from "../../utils/paths";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { BackToHomeButton } from "./components/BackToHomeButton/component";
import { HeroSection } from "./components/HeroSection";
import { PrimaryPropertiesSection } from "./components/PrimaryPropertiesSection";
import { SecondaryPropertiesSection } from "./components/SecondaryPropertiesSection";
import { VibeFeaturedQuote } from "./components/VibeFeaturedQuote";
import { VibePicker } from "./components/VibePicker";
import { VibesErrorModal } from "./components/VibesErrorModal";
import {
  ConnectedWishlistManagementModal,
  WishlistSnackbar,
} from "../wishlist";
import styles from "./styles.module.scss";

export const VibesLandingPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const expState = useExperiments();
  const { matchesMobile } = useDeviceTypes();
  const [isScrolled, setIsScrolled] = useState(false);
  const lastTrackedVibe = useRef<string | null>(null);

  const { data: vibesResponse, isLoading, isError, refetch } =
    useGetVibesQuery();
  const vibes = vibesResponse?.vibes || [];

  const vibesRefreshVariant = getExperimentVariant(
    expState.experiments,
    LC_GATING_EXPERIMENT
  );
  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  useEffect(() => {
    if (vibesRefreshVariant !== AVAILABLE) {
      history.replace(PATH_HOME);
    }
  }, [vibesRefreshVariant, history]);

  useEffect(() => {
    if (!matchesMobile) {
      setIsScrolled(false);
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [matchesMobile]);

  if (vibesRefreshVariant !== AVAILABLE) {
    return null;
  }

  if (isError) {
    return (
      <VibesErrorModal
        hasFailed={isError}
        onRetry={refetch}
        onGoBack={() => history.push(PATH_HOME)}
      />
    );
  }

  const searchParams = new URLSearchParams(location.search);
  const vibeParam = searchParams.get('vibe');

  const currentVibe = !isLoading
    ? vibeParam
      ? vibes.find((vibe) => vibe.vibeName === vibeParam) || vibes[0]
      : vibes[0]
    : undefined;

  if (!isLoading && !currentVibe) {
    return null;
  }

  const handleVibeChange = (vibeName: string) => {
    if (vibeName !== currentVibe?.vibeName) {
      trackEvent({
        eventName: "selected_vibe",
        properties: {
          vibe_name: vibeName,
          screen: "premium_stays_vibes",
          funnel: "premier_collection",
          source: "vibe_picker",
        },
      });
    }
    goToVibe(vibeName);
  };

  const goToVibe = (vibeName: string) => {
    const params = new URLSearchParams(location.search);
    params.set("vibe", vibeName);

    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
    window.scrollTo({ top: 0, behavior: 'auto' });

  };

  const featuredQuote = currentVibe?.basicVibe.featuredQuotes?.[0];

  useEffect(() => {
    const vibeName = currentVibe?.vibeName;
    if (!vibeName || isLoading) {
      return;
    }

    if (lastTrackedVibe.current === vibeName) {
      return;
    }

    trackEvent({
      eventName: "viewed_vibe_page",
      properties: {
        vibe_name: vibeName,
        screen: "premium_stays_vibes",
        funnel: "premier_collection",
      },
    });
    lastTrackedVibe.current = vibeName;
  }, [currentVibe?.vibeName, isLoading]);

  return (
    <Box className={styles.root}>
      <>
          <Box className={styles.heroArea}>
            <Box
              className={clsx(
                styles.heroControls,
                {
                  [styles.mobile]: matchesMobile,
                },
                isLoading && styles.skeleton,
                isLoading && styles.controls
              )}
            >
              {isLoading ? (
                <>
                  <Box className={styles.heroBackButton}>
                    <Skeleton
                      className={styles.backButton}
                      variant="rect"
                    />
                  </Box>
                  <Box className={styles.heroVibePicker}>
                    <Skeleton
                      className={styles.vibePicker}
                      variant="rect"
                    />
                  </Box>
                  <Box className={styles.controlsSpacer} />
                </>
              ) : (
                <>
                  <Box className={styles.heroBackButton}>
                    <BackToHomeButton
                      isMobile={matchesMobile}
                      isScrolled={isScrolled}
                      onClick={() => history.push(PATH_HOME)}
                    />
                  </Box>

                  <Box className={styles.heroVibePicker}>
                    <VibePicker
                      vibes={vibes}
                      selectedVibeName={currentVibe?.vibeName}
                      isMobile={matchesMobile}
                      isScrolled={isScrolled}
                      onChange={handleVibeChange}
                    />
                  </Box>
                </>
              )}
            </Box>

            {isLoading ? (
              <Box
                className={clsx(styles.skeleton, styles.hero, {
                  [styles.mobile]: matchesMobile,
                })}
              >
                <Box className={styles.heroImageArea}>
                  <Skeleton className={styles.heroImage} variant="rect" />
                  <Skeleton className={styles.heroShape} variant="rect" />
                </Box>
                <Box className={styles.heroOverview}>
                  <Skeleton className={styles.eyebrow} />
                  <Skeleton className={styles.description} />
                  <Skeleton className={styles.descriptionShort} />
                </Box>
              </Box>
            ) : (
              currentVibe && (
                <HeroSection
                  isMobile={matchesMobile}
                  vibeOverview={currentVibe.basicVibe.overview}
                />
              )
            )}
          </Box>
          
          {isLoading ? (
            <Box
              className={clsx(styles.skeleton, styles.primary, {
                [styles.mobile]: matchesMobile,
              })}
            >
              <Skeleton className={styles.carousel} variant="rect" />
            </Box>
          ) : (
            currentVibe && (
              <PrimaryPropertiesSection
                properties={currentVibe.primary}
                vibeName={currentVibe.vibeName}
                isMobile={matchesMobile}
                isWishlistExperiment={isWishlistExperiment}
              />
            )
          )}

          {isLoading ? (
            <Box
              className={clsx(styles.skeleton, styles.quote, {
                [styles.mobile]: matchesMobile,
              })}
            >
              <Skeleton className={styles.quoteLine} />
              <Skeleton className={styles.quoteLineShort} />
            </Box>
          ) : (
            !!featuredQuote && (
              <VibeFeaturedQuote
                featuredQuote={featuredQuote}
                isMobile={matchesMobile}
              />
            )
          )}

          {isLoading ? (
            <Box
              className={clsx(styles.skeleton, styles.secondary, {
                [styles.mobile]: matchesMobile,
              })}
            >
              <Skeleton className={styles.sectionTitle} />
              <Box className={styles.cardRow}>
                <Skeleton className={styles.card} variant="rect" />
                <Skeleton className={styles.card} variant="rect" />
                {!matchesMobile && (
                  <Skeleton className={styles.card} variant="rect" />
                )}
              </Box>
            </Box>
          ) : (
            currentVibe && (
              <SecondaryPropertiesSection
                properties={currentVibe.secondary}
                title={currentVibe.basicVibe.secondarySectionTitle}
                vibeName={currentVibe.vibeName}
                isMobile={matchesMobile}
                isWishlistExperiment={isWishlistExperiment}
              />
            )
          )}

          {isLoading ? (
            <Box
              className={clsx(styles.skeleton, styles.cta, {
                [styles.mobile]: matchesMobile,
              })}
            >
              <Skeleton className={styles.sectionTitle} />
              <Skeleton className={styles.ctaSubtitle} />
              <Box className={styles.ctaRow}>
                <Skeleton className={styles.ctaCard} variant="rect" />
                <Skeleton className={styles.ctaCard} variant="rect" />
                {!matchesMobile && (
                  <Skeleton className={styles.ctaCard} variant="rect" />
                )}
              </Box>
            </Box>
          ) : (
            currentVibe && (
              <VibeCallToActionSection
                ctas={currentVibe.basicVibe.ctaMoreDestinations}
                title="More destinations"
                buttonText="Explore"
                onButtonClick={(vibeName) => {
                  goToVibe(vibeName);
                  trackEvent({
                    eventName: "clicked_vibe_entrypoint",
                    properties: {
                      vibe_name: vibeName,
                      screen: "premium_stays_vibes",
                      section: "destinations",
                      funnel: "premier_collection",
                    },
                  });
                }}
              />
            )
          )}
          {isWishlistExperiment && (
            <>
              <ConnectedWishlistManagementModal
                history={history}
                entryType="premium_stays_vibes"
              />
              <WishlistSnackbar />
            </>
          )}
        </>
    </Box>
  );
};
